import { useFocusEffect, useScrollToTop } from '@react-navigation/native';
import { setBadgeCountAsync } from 'expo-notifications';
import type { FC } from 'react';
import { useCallback, useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import type { ScrollView } from 'react-native';
import { SystemBars } from 'react-native-edge-to-edge';
import {
  useAnimatedScrollHandler,
  useSharedValue
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useGetTerritorialAuthority } from '@/features/api/hooks/grapevine-organization/useGetTerritorialAuthority';
import { useGetPerson } from '@/features/api/hooks/participant/useGetPerson';
import { HomeAreaChangeSheet } from '@/features/geo-area/components/subscription/HomeAreaChangeSheet';
import { StartCardCarousel } from '@/features/start/components/header/StartCardCarousel';
import { StartScreenHeader } from '@/features/start/components/header/StartScreenHeader';
import { MeinOrtHappeningsSection } from '@/features/start/components/sections/MeinOrtHappeningsSection';
import { MeinOrtPostsSection } from '@/features/start/components/sections/MeinOrtPostsSection';
import { RegionHappeningsSection } from '@/features/start/components/sections/RegionHappeningsSection';
import { RegionPostsSection } from '@/features/start/components/sections/RegionPostsSection';
import { SponsorSection } from '@/features/start/components/sponsor/SponsorSection';
import type {
  AppNativeStackScreenProps,
  AppRoute
} from '@/navigation/types/navigation';
import { ScrollParallaxGradient } from '@/shared/components/animated/ScrollParallaxGradient';
import { StateView } from '@/shared/components/state-view/StateView';
import { useRefreshActiveQueries } from '@/shared/hooks/useRefreshActiveQueries';
import { isIOS } from '@/shared/utils/platform';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

type Props = AppNativeStackScreenProps<typeof AppRoute.Start>;

const styles = StyleSheet.create({
  screen: { flex: 1 },
  content: { padding: 12, gap: 12 }
});

export const StartScreen: FC<Props> = () => {
  const theme = useAppTheme();

  const { data: person } = useGetPerson();
  const { data: territorialAuthority } = useGetTerritorialAuthority(
    person?.homeAreaId
  );
  const { isRefetching, refetch } = useRefreshActiveQueries();

  const viewRef = useRef<ScrollView>(null);
  useScrollToTop(viewRef);
  const { top } = useSafeAreaInsets();
  const [homeAreaSheetVisible, setHomeAreaSheetVisible] = useState(false);

  const scrollY = useSharedValue(0);
  const onScroll = useAnimatedScrollHandler({
    onScroll: (event) => {
      'worklet';
      scrollY.value = event.contentOffset.y;
    }
  });

  useFocusEffect(
    useCallback(() => {
      SystemBars.setStyle({ statusBar: 'light', navigationBar: 'dark' });
      setBadgeCountAsync(0);
      return () => SystemBars.setStyle('dark');
    }, [])
  );

  return (
    <View
      style={[
        styles.screen,
        { backgroundColor: theme.colors.secondaryContainer }
      ]}
    >
      <ScrollParallaxGradient
        scrollY={scrollY}
        colors={[
          theme.colors.primaryContainer,
          theme.colors.secondaryContainer
        ]}
      />
      <StateView
        ref={viewRef}
        backgroundColor="transparent"
        isRefetching={isRefetching}
        onRefetch={refetch}
        contentContainerStyle={{ paddingTop: top }}
        progressViewOffset={top}
        refreshColor={isIOS ? theme.colors.onPrimary : undefined}
        onScroll={onScroll}
        scrollEventThrottle={16}
      >
        <StartScreenHeader
          homeAreaId={person?.homeAreaId}
          territorialAuthority={territorialAuthority}
          onHomeAreaPress={() => setHomeAreaSheetVisible(true)}
        />
        <View style={styles.content}>
          <StartCardCarousel territorialAuthority={territorialAuthority} />
          <MeinOrtPostsSection />
          <MeinOrtHappeningsSection />
          <RegionPostsSection />
          <RegionHappeningsSection />
          <SponsorSection />
        </View>
      </StateView>
      <HomeAreaChangeSheet
        visible={homeAreaSheetVisible}
        onDismiss={() => setHomeAreaSheetVisible(false)}
      />
    </View>
  );
};
